/**
 * CSD Public SDK v3.0
 * 主入口文件
 * @module csd-public
 */

import type { CSDConfig } from './types/config'
import type { PaymentChannelAdapter } from './types/payment'
import type { HttpConfig } from './types/api'
import { createStorage } from './core/storage'
import { createHttpClient } from './core/http'
import { AuthService } from './core/services/AuthService'
import { UserService } from './core/services/UserService'
import { PaymentService } from './core/services/PaymentService'
import { ProductService } from './core/services/ProductService'
import { InviteService } from './core/services/InviteService'
import { TaskService } from './core/services/TaskService'
import { UploadService } from './core/services/UploadService'
import { EventService } from './core/services/EventService'
import { DeviceInfoManager } from './core/services/DeviceInfoManager'
import { HookManager } from './core/hooks'
import { DeviceStore } from './core/stores/DeviceStore'
import { SourceStore } from './core/stores/SourceStore'
import { EventTrackingStore } from './core/stores/EventTrackingStore'
import { createParamsInterceptor } from './core/http/interceptors/params'
import { createUseAuth } from './adapters/vue/composables/useAuth'
import { createUseUser } from './adapters/vue/composables/useUser'
import { createUsePayment } from './adapters/vue/composables/usePayment'
import { createUseInvite } from './adapters/vue/composables/useInvite'
import { createUsePhoneLogin } from './composables/usePhoneLogin'
import { createUseQRLogin } from './composables/useQRLogin'
import { createUsePaymentFlow } from './composables/usePaymentFlow'
import { createUseTaskPolling } from './composables/useTaskPolling'
import { EventAPI } from './core/api/event'
import { API_ENDPOINTS } from './constants/api-endpoints'
import { initSDKConfig } from './core/config/SDKConfigManager'
import {
  getStoragePrefix as _getStoragePrefix,
  getSoftId as _getSoftId
} from './core/config/SDKConfigManager'

// ============================================
// CSD 客户端类
// ============================================

/**
 * CSD 客户端
 * SDK 的核心类，提供所有服务和功能的访问入口
 */
export class CSDClient {
  // 核心服务
  public readonly authService: AuthService
  public readonly userService: UserService
  public readonly paymentService: PaymentService
  public readonly productService: ProductService
  public readonly inviteService: InviteService
  public readonly taskService: TaskService
  public readonly uploadService: UploadService
  public readonly eventService: EventService
  public readonly hookManager: HookManager

  // 配置 Store
  public readonly deviceStore: DeviceStore
  public readonly sourceStore: SourceStore
  public readonly eventTrackingStore: EventTrackingStore

  // 基础 Vue Composables（兼容旧版）
  public readonly useAuth: ReturnType<typeof createUseAuth>
  public readonly useUser: ReturnType<typeof createUseUser>
  public readonly usePayment: ReturnType<typeof createUsePayment>
  public readonly useInvite: ReturnType<typeof createUseInvite>

  // 新版 Composables（完整流程封装）
  public readonly usePhoneLogin: ReturnType<typeof createUsePhoneLogin>
  public readonly useQRLogin: ReturnType<typeof createUseQRLogin>
  public readonly usePaymentFlow: ReturnType<typeof createUsePaymentFlow>
  public readonly useTaskPolling: ReturnType<typeof createUseTaskPolling>

  private config: CSDConfig

  constructor(config: CSDConfig) {
    this.config = config
    const storagePrefix = config.storagePrefix ?? 'csd_'

    // 初始化 SDK 全局配置（必须在其他初始化之前）
    initSDKConfig({
      softId: config.softId || '',
      storagePrefix,
      version: config.version || '1.0.0.0',
      os: config.os || 1,
      enablePromotionParams: config.enablePromotionParams !== false,
      wxLoginUrl: config.wxLoginUrl,
      wxLoginAndPayDomain: config.wxLoginAndPayDomain,
      applet: config.applet,
      virtualPayEnv: config.virtualPayEnv,
      taskAPI: config.taskAPI,
      endpoints: config.endpoints,
      payment: config.payment
    })

    // 创建 Hooks 管理器
    this.hookManager = new HookManager(config.hooks)

    // 创建存储实例
    const storage = createStorage({
      prefix: storagePrefix
    })

    // 创建 HTTP 客户端配置
    const httpConfig: HttpConfig = {
      baseURL: config.baseURL,
      timeout: config.timeout || 30000,
      headers: config.headers || {}
    }

    // 创建 HTTP 客户端
    const httpClient = createHttpClient(httpConfig, storage, {
      softId: config.softId,
      storagePrefix,
      version: config.version || '1.0.0.0',
      os: config.os || 1
    })

    // 创建服务实例
    const enableTokenRefresh = config.enableTokenRefresh === true
    this.authService = new AuthService(httpClient, storage, { enableTokenRefresh })

    if (enableTokenRefresh) {
      // 调用方显式开启刷新时，业务请求需等待初始化/登录轮换完成，避免继续携带旧 token。
      httpClient.addRequestInterceptor(async (request) => {
        const isRefreshRequest =
          request.url.includes(API_ENDPOINTS.AUTH.REFRESH_TOKEN) ||
          request.url.includes(API_ENDPOINTS.AUTH.REFRESH_SESSION)
        if (!isRefreshRequest) {
          await this.authService.waitForTokenRefresh()
        }
        return request
      })
      httpClient.prependErrorInterceptor(async (error) => {
        const response = (error as any)?.response
        const request = response?.config
        const status = response?.status
        const isAuthError = status === 401 || status === 403 || response?.data?.code === 403
        const isRefreshRequest = request?.url?.includes(API_ENDPOINTS.AUTH.REFRESH_SESSION)
        if (!request || !isAuthError || isRefreshRequest || request._authRetried === true) {
          throw error
        }

        const requestToken = request.headers?.Authorization || request.headers?.authorization
        const currentToken = this.authService.getToken()
        if (!requestToken || !currentToken || requestToken !== currentToken) {
          throw error
        }

        await this.authService.refreshToken()
        const nextToken = this.authService.getToken()
        if (!nextToken || nextToken === requestToken) {
          throw error
        }

        return httpClient.request({
          ...request,
          _authRetried: true,
          headers: {
            ...request.headers,
            Authorization: nextToken
          }
        })
      })
    }

    this.userService = new UserService(httpClient, storage)
    this.paymentService = new PaymentService(httpClient, storage)
    this.productService = new ProductService(httpClient)
    this.inviteService = new InviteService(httpClient, storage)
    this.taskService = new TaskService(httpClient, storage)
    this.uploadService = new UploadService(httpClient, storage)

    // 创建事件上报服务
    const eventAPI = new EventAPI(httpClient)
    this.eventService = new EventService(eventAPI)

    // 创建配置 Store
    this.deviceStore = new DeviceStore(storage)
    this.sourceStore = new SourceStore(storage)
    this.eventTrackingStore = new EventTrackingStore(storage)

    // 初始化设备信息
    if (config.softId) {
      this.deviceStore.initialize({ softId: config.softId })
    }

    // 注入公共参数/推广参数拦截器（兼容 v2 行为）
    const deviceInfoManager = new DeviceInfoManager(this.deviceStore, this.sourceStore, storage)
    httpClient.addRequestInterceptor(
      createParamsInterceptor(deviceInfoManager, storage, {
        enablePromotionParams: config.enablePromotionParams !== false,
        getToken: () => this.authService.getToken()
      })
    )

    if (enableTokenRefresh) {
      this.authService.refreshExistingTokenOnInit()
    }

    // 创建基础 Vue Composables
    this.useAuth = createUseAuth(this.authService)
    this.useUser = createUseUser(this.userService)
    this.usePayment = createUsePayment(this.paymentService)
    this.useInvite = createUseInvite(this.inviteService)

    // 创建新版 Composables
    this.usePhoneLogin = createUsePhoneLogin(this.authService)
    this.useQRLogin = createUseQRLogin(this.authService)
    this.usePaymentFlow = createUsePaymentFlow(this.paymentService)
    this.useTaskPolling = createUseTaskPolling(this.taskService)
  }

  /** 获取认证服务 */
  getAuthService() {
    return this.authService
  }

  /** 获取用户服务 */
  getUserService() {
    return this.userService
  }

  /** 获取支付服务 */
  getPaymentService() {
    return this.paymentService
  }

  /** 注册宿主提供的外部支付渠道。 */
  registerPaymentChannel(channel: PaymentChannelAdapter) {
    return this.paymentService.registerPaymentChannel(channel)
  }

  /** 释放当前外部支付渠道。 */
  releasePaymentChannel() {
    return this.paymentService.releasePaymentChannel()
  }

  /** 获取产品服务 */
  getProductService() {
    return this.productService
  }

  /** 获取产品信息（异步更新，兼容旧版） */
  getProductInfo() {
    return this.productService.getProductInfo()
  }

  /** 获取产品信息（同步风格，返回 Promise） */
  getProductInfoSync() {
    return this.productService.getProductInfoSync()
  }

  /** 获取邀请服务 */
  getInviteService() {
    return this.inviteService
  }

  /** 获取任务服务 */
  getTaskService() {
    return this.taskService
  }

  /** 获取上传服务 */
  getUploadService() {
    return this.uploadService
  }

  /** 获取事件上报服务 */
  getEventService() {
    return this.eventService
  }

  /** 获取设备配置 Store */
  getDeviceStore() {
    return this.deviceStore
  }

  /** 获取来源追踪 Store */
  getSourceStore() {
    return this.sourceStore
  }

  /** 获取事件追踪 Store */
  getEventTrackingStore() {
    return this.eventTrackingStore
  }

  /** 检查是否已登录 */
  isLogin() {
    return this.authService.isLogin()
  }

  /** 获取当前用户信息 */
  getUserInfo() {
    return this.authService.getUserInfo()
  }

  /** 获取当前 Token */
  getToken() {
    return this.authService.getToken()
  }

  /** 获取 Hooks 管理器 */
  getHookManager() {
    return this.hookManager
  }

  /** 注册 Hooks */
  registerHooks(hooks: CSDConfig['hooks']) {
    if (hooks) {
      this.hookManager.register(hooks)
    }
  }

  /** 获取配置 */
  getConfig() {
    return this.config
  }

  /**
   * 初始化 SDK
   * 应在 SDK 创建后立即调用，触发事件上报
   */
  async initialize(options?: { enableEventReport?: boolean }): Promise<void> {
    try {
      if (typeof window !== 'undefined') {
        this.sourceStore.initializeFromUrl(window.location.href)
      }

      const bdVid = this.sourceStore.bdVid
      const strategy = this.sourceStore.strategy

      // 事件上报是旁路能力，不能因为埋点接口超时阻塞页面首屏。
      void this.eventService.initialize({
        bdVid: bdVid || undefined,
        strategy: strategy || undefined,
        enableEventReport: options?.enableEventReport !== false
      })
    } catch (error) {
      console.error('[CSDClient] Failed to initialize', error)
    }
  }
}

/**
 * 创建 CSD 客户端实例
 * @param config - SDK 配置
 */
export function createCSDClient(config: CSDConfig): CSDClient {
  return new CSDClient(config)
}

// ============================================
// 导出类型
// ============================================

export * from './types'

// 从 constants 导出，但排除已在 types/payment.ts 中重新导出的枚举
export {
  // errors
  ERROR_CODES,
  ERROR_MESSAGES,
  QNW_SSO_ERROR_CODES,
  QNW_SSO_ERROR_MESSAGES,
  // storage-keys
  STORAGE_KEYS,
  // api-endpoints
  API_ENDPOINTS,
  // payment（排除 ValidityType, SoftEdition, PricePlanType，因为已在 types/payment.ts 中导出）
  VALIDITY_TYPE_TIME,
  VIP_VERSION,
  USER_EDITION,
  getValidityDays,
  getVipVersionName,
  getUserEditionName
} from './constants'
export type {
  VipVersionKey,
  VipVersionValue,
  UserEditionKey,
  UserEditionValue
} from './constants/payment'

// ============================================
// 导出核心服务
// ============================================

export { AuthService } from './core/services/AuthService'
export { UserService } from './core/services/UserService'
export { PaymentService } from './core/services/PaymentService'
export { ProductService } from './core/services/ProductService'
export { InviteService } from './core/services/InviteService'
export { TaskService } from './core/services/TaskService'
export { UploadService } from './core/services/UploadService'
export { EventService } from './core/services/EventService'

// ============================================
// 导出 Store
// ============================================

export { AuthStore } from './core/stores/AuthStore'
export { UserStore } from './core/stores/UserStore'
export { PaymentStore } from './core/stores/PaymentStore'
export { InviteStore } from './core/stores/InviteStore'
export { DeviceStore } from './core/stores/DeviceStore'
export { SourceStore } from './core/stores/SourceStore'
export { EventTrackingStore } from './core/stores/EventTrackingStore'

// ============================================
// 导出 API
// ============================================

export { AuthAPI } from './core/api/auth'
export { UserAPI } from './core/api/user'
export { PaymentAPI } from './core/api/payment'
export { InviteAPI } from './core/api/invite'
export { TaskAPI } from './core/api/task'
export { UploadAPI } from './core/api/upload'
export { EventAPI } from './core/api/event'
export { ProductAPI } from './core/api/product'

// ============================================
// 导出 Hooks
// ============================================

export { HookManager, createHookManager } from './core/hooks'
export type { HookConfig, HookFn } from './core/hooks'

// ============================================
// 导出工具类
// ============================================

export { Polling, createPolling, poll } from './core/utils/polling'
export { Countdown, createCountdown } from './core/utils/countdown'
export * from './core/utils/validator'
export * from './core/utils/device'

// ============================================
// 导出存储模块
// ============================================

export { createStorage, createMemoryStorage, detectEnvironment } from './core/storage'
export { UniAppStorage } from './core/storage/UniAppStorage'
export { BrowserStorage } from './core/storage/BrowserStorage'
export type { IStorage, StorageOptions, StorageItem } from './core/storage/Storage'

// ============================================
// 导出 Vue 适配器
// ============================================

export * from './adapters/vue'

// ============================================
// 导出 HTTP 相关
// ============================================

export { setLoginRequiredCallback, resetLoginRequiredFlag, setStoragePrefix } from './core/http'

// ============================================
// 导出配置管理器
// ============================================

export {
  initSDKConfig,
  updateSDKConfig,
  getSDKConfig,
  getSoftId,
  getStoragePrefix,
  getStorageKey,
  getVersion,
  getOS,
  isPromotionParamsEnabled,
  getWxLoginUrl,
  getWxLoginAndPayDomain,
  getApplet,
  getVirtualPayEnv,
  getPaymentConfig,
  getSdk360PaymentConfig,
  getTaskAPIConfig,
  getEndpoint,
  getAllEndpoints,
  onConfigUpdate,
  validateSDKConfig,
  resetSDKConfig
} from './core/config/SDKConfigManager'

// ============================================
// 导出 TaskAPI 配置
// ============================================

export {
  DEFAULT_TASK_API_CONFIG,
  validateTaskAPIConfig,
  mergeTaskAPIConfig,
  replaceEndpointParams
} from './core/config/TaskAPIConfig'
export type { TaskAPIConfig, TaskAPIEndpoints } from './core/config/TaskAPIConfig'

// ============================================
// 导出错误处理模块
// ============================================

export {
  SDKErrorType,
  SDKException,
  createConfigError,
  createDeviceError,
  createNetworkError,
  createAuthError,
  createParamError,
  createBusinessError,
  isSDKError,
  fromUnknownError,
  ErrorHandler,
  getErrorHandler,
  initErrorHandler,
  resetErrorHandler
} from './core/errors'
export type { SDKError, ErrorHandlerConfig } from './core/errors'

// ============================================
// 全局认证参数获取
// ============================================

/**
 * 获取认证参数（供外部 HTTP 客户端使用）
 * 从 localStorage 读取 SDK 存储的设备信息和 Token
 */
export function getAuthParams(): {
  ekey: string
  device_code: string
  soft_id: string
  token: string
  source: string
  qhclickid?: string
  bdVid?: string
  strategy?: string
  mp?: string
  invite_id?: number
} {
  if (typeof window === 'undefined') {
    return { ekey: '', device_code: '', soft_id: '', token: '', source: 'default' }
  }

  const storagePrefix = _getStoragePrefix()

  // 读取设备信息（key: {prefix}csd_device_code，与 v2 保持一致）
  const device = parseStorageItem<{ deviceCode?: string; ekey?: string; softId?: string }>(
    `${storagePrefix}csd_device_code`
  )
  const legacyDeviceCode = parseLegacyItem('deviceCode')
  const legacyEkey = parseLegacyItem('ekey')
  const legacySoftId = parseLegacyItem('soft_id')

  // 读取 Token（key: {prefix}token）
  const auth = parseStorageItem<{ token?: string }>(`${storagePrefix}token`)
  const legacyToken = parseLegacyItem('token')

  // 读取来源信息（key: {prefix}source）
  const sourceState = parseStorageItem<{
    source?: string
    qhclickid?: string
    bdVid?: string
    strategy?: string
    mp?: string
  }>(`${storagePrefix}source`)
  const legacySource = parseLegacyItem('source')
  const legacyQhclickid = parseLegacyItem('qhclickid')
  const legacyBdVid = parseLegacyItem('bdVid') || parseLegacyItem('bd_vid')
  const legacyStrategy = parseLegacyItem('strategy')
  const legacyMp = parseLegacyItem('mp')

  // 读取 invite_id（key: {prefix}invite_id）
  const inviteIdRaw = parseStorageItem<number | string>(`${storagePrefix}invite_id`)
  const legacyInviteRaw = parseLegacyItem('invite_id')
  const inviteIdParsed = typeof inviteIdRaw === 'string' ? parseInt(inviteIdRaw, 10) : inviteIdRaw
  const legacyInviteParsed =
    typeof legacyInviteRaw === 'string' ? parseInt(legacyInviteRaw, 10) : legacyInviteRaw
  const inviteIdValue =
    typeof inviteIdParsed === 'number' && !Number.isNaN(inviteIdParsed)
      ? inviteIdParsed
      : typeof legacyInviteParsed === 'number' && !Number.isNaN(legacyInviteParsed)
        ? legacyInviteParsed
        : undefined

  return {
    ekey: device?.ekey || legacyEkey || '',
    device_code: device?.deviceCode || legacyDeviceCode || '',
    soft_id: device?.softId || legacySoftId || _getSoftId() || '',
    token: auth?.token || legacyToken || '',
    source: sourceState?.source || legacySource || 'default',
    qhclickid: sourceState?.qhclickid || legacyQhclickid,
    bdVid: sourceState?.bdVid || legacyBdVid,
    strategy: sourceState?.strategy || legacyStrategy,
    mp: sourceState?.mp || legacyMp,
    invite_id: inviteIdValue
  }
}

/**
 * 解析 SDK 存储格式
 * SDK 使用 BrowserStorage 包装: { value: T | string, timestamp: number }
 */
function parseStorageItem<T>(key: string): T | null {
  try {
    const data = localStorage.getItem(key)
    if (!data) return null

    const wrapper = JSON.parse(data)
    if (wrapper.value !== undefined) {
      return typeof wrapper.value === 'string' ? JSON.parse(wrapper.value) : wrapper.value
    }
    return wrapper as T
  } catch {
    return null
  }
}

/**
 * 读取旧版 localStorage（无前缀）
 */
function parseLegacyItem(key: string): any | null {
  try {
    const data = localStorage.getItem(key)
    if (!data) return null
    try {
      const parsed = JSON.parse(data)
      if (parsed?.value !== undefined) {
        const value = parsed.value
        if (typeof value === 'string') {
          try {
            const nested = JSON.parse(value)
            if (nested?.token) {
              return nested.token
            }
            return nested
          } catch {
            return value
          }
        }
        if (value?.token) {
          return value.token
        }
        return value
      }
      if (parsed?.token) {
        return parsed.token
      }
      return parsed
    } catch {
      return data
    }
  } catch {
    return null
  }
}
